import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Coins, Warning } from '@phosphor-icons/react';
import useAuthStore from '../store/authStore';
import { PageSkeleton } from '../components/Skeletons';
import ProfitSettingsPanel from '../components/profit/ProfitSettingsPanel';
import ProfitWaterfall, { inr } from '../components/profit/ProfitWaterfall';
import ProfitTable from '../components/profit/ProfitTable';
import ProfitOrdersTable from '../components/profit/ProfitOrdersTable';
import ExportButtons from '../components/profit/ExportButtons';
import BudgetVsActual from '../components/profit/BudgetVsActual';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const TABS = [['salesperson', 'By salesperson'], ['region', 'By region'], ['month', 'By month'], ['orders', 'Orders']];

const DataQuality = ({ dq }) => {
  const notes = [];
  if (dq.lines_without_cogs) notes.push(`${dq.lines_without_cogs} line(s) without COGS left out (${inr(dq.revenue_without_cogs)} revenue)`);
  if (dq.orders_with_estimated_freight) notes.push(`${dq.orders_with_estimated_freight} order(s) use transport charged as freight estimate`);
  if (dq.salaries_entered < dq.team_size) notes.push(`salaries entered for ${dq.salaries_entered} of ${dq.team_size} team members`);
  if (dq.office_cost_months_missing.length) notes.push(`no office costs for ${dq.office_cost_months_missing.length} month(s)`);
  if (dq.orders_skipped_no_quote) notes.push(`${dq.orders_skipped_no_quote} won order(s) without quote data skipped`);
  if (!notes.length) return null;
  return (
    <div className="flex gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50 text-xs text-amber-900" data-testid="profit-data-quality">
      <Warning size={16} className="shrink-0 mt-0.5" />
      <span><b>Estimate accuracy:</b> {notes.join(' · ')}.</span>
    </div>
  );
};

export default function Profitability() {
  const { user } = useAuthStore();
  const canView = ['Vice President', 'President'].includes(user?.role);
  const now = new Date();
  const [range, setRange] = useState({ start: ym(new Date(now.getFullYear(), now.getMonth() - 11, 1)), end: ym(now) });
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('salesperson');

  const load = useCallback(() => {
    axios.get(`${API}/profitability/report`, { params: { start_month: range.start, end_month: range.end } })
      .then((r) => { setData(r.data); setError(null); })
      .catch((e) => setError(e.response?.data?.detail || 'Failed to load profitability'));
  }, [range.start, range.end]);

  useEffect(() => { if (canView) load(); }, [canView, load]);

  if (!canView) return <div className="p-8 text-[#6D8E87]" data-testid="profit-no-access">Profitability is available to VP and President only.</div>;
  if (!data && !error) return <PageSkeleton />;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="profitability-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="app-page-title flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><Coins size={30} className="text-[#00a69c]" /> Profitability</h1>
          <p className="text-[#6D8E87] text-sm">Closest estimate of net profit on won orders — counted in the month each order was won.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <input type="month" value={range.start} max={range.end} onChange={(e) => setRange({ ...range, start: e.target.value })}
            className="px-3 py-2 border border-[#D5E8E4] rounded-lg" data-testid="profit-start-month" />
          <span className="text-[#6D8E87]">to</span>
          <input type="month" value={range.end} min={range.start} onChange={(e) => setRange({ ...range, end: e.target.value })}
            className="px-3 py-2 border border-[#D5E8E4] rounded-lg" data-testid="profit-end-month" />
          <ExportButtons range={range} />
        </div>
      </div>

      {error && <p className="text-sm text-rose-600" data-testid="profit-error">{error}</p>}
      {data && (
        <>
          <ProfitSettingsPanel settings={data.settings} onSaved={load} />
          <DataQuality dq={data.data_quality} />
          <ProfitWaterfall t={data.totals} />
          <BudgetVsActual months={data.by_month} totals={data.totals} targets={data.settings.yearly_targets || []}
            canEdit={user?.role === 'President'} onSaved={load} />
          <div className="medical-card">
            <div className="flex gap-1 p-2 border-b border-[#D5E8E4]">
              {TABS.map(([k, l]) => (
                <button key={k} type="button" onClick={() => setTab(k)} data-testid={`profit-tab-${k}`}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === k ? 'bg-[#00a69c] text-white' : 'text-[#3B5A54] hover:bg-[#F4F9F7]'}`}>{l}</button>
              ))}
            </div>
            {tab === 'salesperson' && <ProfitTable rows={data.by_salesperson} by="salesperson" />}
            {tab === 'region' && <ProfitTable rows={data.by_region} by="region" />}
            {tab === 'month' && <ProfitTable rows={data.by_month} by="month" />}
            {tab === 'orders' && <ProfitOrdersTable orders={data.orders} />}
          </div>
          <p className="text-xs text-[#6D8E87]">
            Salespeople's own salaries are charged against their own orders. Other staff salaries and office costs are shared by revenue.
            Salaries are entered on each person's profile under My Team.
          </p>
        </>
      )}
    </div>
  );
}
